import { FC } from 'react'

import { FormProvider, useForm } from 'react-hook-form'
import * as R from 'remeda'

import { LoadingContainer } from 'peach/components'
import { StyledButton as Button } from 'peach/components/Button/Button'
import Input from 'peach/components/Input'

import { ErrorComponent, FormActions, Title } from './Components'
import { Company } from './types'
import { useCompanyAppDomainPost } from 'core/types'

type Domain = {
  type: 'borrower' | 'agent' | 'admin'
  domain: string
}

type CreateAppDomainsFormValues = {
  domains: Array<Domain>
}

type CreateAppDomainsFormProps = {
  company: Company
  updateCompany: (obj: Company) => void
  title: string
}

const CreateAppDomainsForm: FC<CreateAppDomainsFormProps> = ({
  company,
  updateCompany,
  title,
}) => {
  const form = useForm<CreateAppDomainsFormValues>({
    values: {
      domains: [
        {
          type: 'borrower',
          domain: '',
        },
        {
          type: 'agent',
          domain: '',
        },
        {
          type: 'admin',
          domain: '',
        },
      ],
    },
  })

  const { domains } = form.getValues()

  const {
    mutateAsync: createAppDomains,
    isPending,
    isError,
    error,
  } = useCompanyAppDomainPost({
    companyId: company?.companyId,
  })

  const onCreate = async (values) => {
    const responses = await Promise.all(
      R.map(values.domains as Array<Domain>, (d) =>
        createAppDomains({
          type: d.type,
          domain: d.domain,
        }),
      ),
    )

    if (responses.every((response) => response.status === 201)) {
      updateCompany({
        ...company,
        appDomains: [
          ...(company?.appDomains ?? []),
          ...(R.map(responses, (response) => ({
            id: response.data.id,
            type: response.data.type,
            domain: response.data.domain,
          })) ?? []),
        ],
      })
    }
  }

  return (
    <LoadingContainer loading={isPending}>
      <Title>{title}</Title>
      <form
        onSubmit={form.handleSubmit(async (values) => {
          try {
            await onCreate(values)
          } catch (e) {
            console.error('Error with create actions: ', e)
          }
        })}
      >
        <FormProvider {...form}>
          {R.map.indexed(domains, (domain, index) => (
            <Input
              key={domain.type}
              label={`${domain.type} domain`}
              placeholder={
                domain.type === 'borrower'
                  ? 'lender-borrower.peach-sandbox.finance'
                  : domain.type === 'agent'
                  ? 'lender-agent.peach-sandbox.finance'
                  : 'lender-admin.peach-sandbox.finance'
              }
              required
              type='text'
              {...form.register(`domains.${index}.domain`)}
            />
          ))}

          {isError &&
            !!error &&
            typeof error === 'object' &&
            'message' in error &&
            typeof error.message === 'string' && (
              <ErrorComponent>{error.message}</ErrorComponent>
            )}
          <FormActions>
            <Button $primary type='submit'>
              Submit
            </Button>
          </FormActions>
        </FormProvider>
      </form>
    </LoadingContainer>
  )
}

export default CreateAppDomainsForm
